// Shared Support-Ticket detail view — used by BOTH client & admin routes.
// Identical layout & styling; admin-only controls (status/assign/link-order)
// render only when `isAdmin` is true.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MessageCircle, Send, Building2, Link as LinkIcon, X, User as UserIcon, Package, Clock, Paperclip, Download, FileText, ImageIcon, Bell, Lock, AlertOctagon, Calendar, Trash2, Check, UserX, Search } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { DueDatePicker } from './DueDatePicker';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { ticketsApi } from '../../services/api';
import api from '../../services/api';
import { getApiError } from '../../utils/errorHelper';

const STATUS_META = {
  open: { label: 'Open', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' },
  resolved: { label: 'Resolved', bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30' },
  closed: { label: 'Closed', bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

const VALID_NEXT = {
  open: ['in_progress'],
  in_progress: ['resolved'],
  resolved: ['closed', 'in_progress'],
  closed: ['in_progress'],
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  // Force IST — regardless of the browser's system timezone. Snigdha360 runs
  // out of India, so every timestamp on-screen should read in Asia/Kolkata.
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

// ── Multi-file composer (admin-only queue) ─────────────────────────────
// Kept in sync with backend BATCH_ALLOWED_EXTENSIONS + BATCH_MAX_FILES /
// BATCH_MAX_MB_PER_FILE constants in /app/backend/routes/tickets.py.
const MAX_BATCH_FILES = 10;
const MAX_BATCH_MB = 10;
const ALLOWED_EXTS = new Set([
  'pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp',
  'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt',
]);
function _extOf(name) {
  if (!name || !name.includes('.')) return '';
  return name.split('.').pop().toLowerCase();
}
function _validateBatchFile(f) {
  if (!f || f.size === 0) return 'File is empty';
  if (f.size > MAX_BATCH_MB * 1024 * 1024) return `Exceeds ${MAX_BATCH_MB} MB limit`;
  const ext = _extOf(f.name || '');
  if (!ext || !ALLOWED_EXTS.has(ext)) return 'File type not allowed';
  return null;
}
function _pasteFilename() {
  const d = new Date();
  const z = (n) => String(n).padStart(2, '0');
  return `paste-${d.getFullYear()}${z(d.getMonth() + 1)}${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}${z(d.getSeconds())}.png`;
}
function _fmtSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
function _iconFor(name, mime) {
  if ((mime && /^image\//.test(mime)) || /\.(png|jpe?g|gif|webp)$/i.test(name)) return { cls: 'bg-sky-600/70', label: 'IMG' };
  if ((mime && /pdf/i.test(mime)) || /\.pdf$/i.test(name)) return { cls: 'bg-red-600/70', label: 'PDF' };
  if (/\.(xlsx?|csv)$/i.test(name)) return { cls: 'bg-emerald-700/80', label: 'XLS' };
  if (/\.(docx?|txt)$/i.test(name)) return { cls: 'bg-indigo-600/70', label: 'DOC' };
  return { cls: 'bg-slate-600/70', label: 'FILE' };
}

export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${meta.bg} ${meta.text} border ${meta.border}`}
      data-testid={`ticket-status-badge-${status}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.text.replace('text-', 'bg-')}`} />
      {meta.label}
    </span>
  );
};

const AVATAR_TONES = [
  { bg: 'rgba(16,185,129,0.25)', fg: '#a7f3d0' },
  { bg: 'rgba(14,165,233,0.25)', fg: '#bae6fd' },
  { bg: 'rgba(245,158,11,0.25)', fg: '#fde68a' },
  { bg: 'rgba(139,92,246,0.25)', fg: '#ddd6fe' },
  { bg: 'rgba(244,63,94,0.25)', fg: '#fecdd3' },
];
function _avatarTone(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}
function _initials(name) {
  return String(name || '?').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

// Themed assignee picker used inside the ticket detail's admin controls.
// Uses inline styles for the popover background so the dark theme is guaranteed
// regardless of shadcn/Radix's default CSS variables (they used to bleed a
// white background from `--popover` in the portal-rendered content).
const AssignPicker = ({ value, admins, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = admins.find(a => a.id === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter(a => (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q));
  }, [admins, query]);
  const tone = current ? _avatarTone(current.name) : null;
  const commit = (id) => { onChange(id); setOpen(false); setQuery(''); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex items-center gap-2 px-2 py-1 text-xs rounded-md border border-emerald-900/30 hover:border-emerald-500/50 min-w-[160px]"
          style={{ background: '#0f1611', color: '#e2e8f0' }}
          data-testid="ticket-assign-trigger"
        >
          {current ? (
            <span
              className="flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold flex-shrink-0"
              style={{ background: tone.bg, color: tone.fg }}
            >{_initials(current.name)}</span>
          ) : (
            <UserX className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          )}
          <span className="truncate flex-1 text-left">{current ? current.name : <span className="text-slate-500 italic">Unassigned</span>}</span>
          <ChevronDownIcon />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-0 border-emerald-900/40 shadow-2xl"
        // Inline styles so the portal-rendered content is definitively dark, no
        // matter what `--popover` resolves to in the current theme.
        style={{ background: '#0a0f0d', color: '#e2e8f0' }}
      >
        <div className="relative border-b border-emerald-900/40">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search teammates…"
            className="w-full pl-8 pr-3 py-2 text-xs bg-transparent text-white placeholder-slate-500 focus:outline-none"
            data-testid="ticket-assign-search"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => commit(null)}
            className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-emerald-500/10"
            data-testid="ticket-assign-option-unassign"
          >
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-700/40 text-slate-500 flex-shrink-0"><UserX className="w-3 h-3" /></span>
            <span className="text-xs text-slate-400 italic flex-1">Unassigned</span>
            {!value && <Check className="w-3.5 h-3.5 text-emerald-400" />}
          </button>
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-[11px] text-slate-500 italic">No teammates match &ldquo;{query}&rdquo;.</p>
          ) : (
            filtered.map(a => {
              const t = _avatarTone(a.name);
              const isCurrent = a.id === value;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => commit(a.id)}
                  className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-emerald-500/10"
                  data-testid={`ticket-assign-option-${a.id}`}
                >
                  <span
                    className="flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold flex-shrink-0"
                    style={{ background: t.bg, color: t.fg }}
                  >{_initials(a.name)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-200 truncate">{a.name}</div>
                    {a.email && <div className="text-[10px] text-slate-500 truncate">{a.email}</div>}
                  </div>
                  {isCurrent && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
// Tiny caret used by the trigger button
const ChevronDownIcon = () => (
  <svg className="w-3 h-3 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
);

const AdminControls = ({ ticket, onRefresh, admins, orders, onNavigateToOrder }) => {
  const [saving, setSaving] = useState(null);
  const [orderPickerOpen, setOrderPickerOpen] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [blockerPickerOpen, setBlockerPickerOpen] = useState(false);
  const [blockerSearch, setBlockerSearch] = useState('');

  const nextStatuses = VALID_NEXT[ticket.status] || [];

  const doStatus = async (s) => {
    setSaving('status');
    try {
      await ticketsApi.changeStatus(ticket.id, s);
      toast.success(`Status changed to ${STATUS_META[s].label}`);
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to change status')); }
    finally { setSaving(null); }
  };
  const doAssign = async (adminId) => {
    setSaving('assign');
    try {
      await ticketsApi.assign(ticket.id, adminId || null);
      toast.success(adminId ? 'Ticket assigned' : 'Unassigned');
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to assign')); }
    finally { setSaving(null); }
  };
  const doLink = async (orderId) => {
    setSaving('link');
    try {
      await ticketsApi.linkOrder(ticket.id, orderId);
      toast.success(orderId ? 'Order linked' : 'Order link removed');
      setOrderPickerOpen(false);
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to update linked order')); }
    finally { setSaving(null); }
  };
  const doBlockedBy = async (orderId) => {
    setSaving('blocked');
    try {
      await ticketsApi.setBlockedBy(ticket.id, orderId);
      toast.success(orderId ? 'Blocker set' : 'Blocker cleared');
      setBlockerPickerOpen(false);
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to update blocker')); }
    finally { setSaving(null); }
  };
  const doDueDate = async (date) => {
    setSaving('due');
    try {
      await ticketsApi.setDueDate(ticket.id, date);
      toast.success(date ? 'Due date updated' : 'Due date cleared');
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to update due date')); }
    finally { setSaving(null); }
  };
  const doReminder = async () => {
    if (!window.confirm('Send a reminder email to the client asking for their input?')) return;
    setSaving('reminder');
    try {
      const res = await api.post(`/tickets/${ticket.id}/send-reminder`);
      toast.success(`Reminder sent to ${res.data?.recipient || 'client'}`);
      await onRefresh();
    } catch (e) { toast.error(getApiError(e, 'Failed to send reminder')); }
    finally { setSaving(null); }
  };

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(o =>
      (o.product_name || '').toLowerCase().includes(q) ||
      (o.period || '').toLowerCase().includes(q)
    );
  }, [orders, orderSearch]);

  const filteredBlockerOrders = useMemo(() => {
    const q = blockerSearch.trim().toLowerCase();
    // Exclude the ticket's own linked order — it can't block itself
    const pool = ticket.order_id ? orders.filter(o => o.id !== ticket.order_id) : orders;
    if (!q) return pool;
    return pool.filter(o =>
      (o.product_name || '').toLowerCase().includes(q) ||
      (o.period || '').toLowerCase().includes(q)
    );
  }, [orders, blockerSearch, ticket.order_id]);

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" data-testid="ticket-admin-controls">
      {/* Status dropdown */}
      <div className="flex items-center gap-2">
        <span className="text-slate-400 text-xs">Status:</span>
        <select
          disabled={saving === 'status' || nextStatuses.length === 0}
          value=""
          onChange={(e) => e.target.value && doStatus(e.target.value)}
          className="bg-[#0f1611] border border-emerald-900/30 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500"
          data-testid="ticket-status-select"
        >
          <option value="">{STATUS_META[ticket.status].label}</option>
          {nextStatuses.map(s => (
            <option key={s} value={s}>Change to {STATUS_META[s].label}</option>
          ))}
        </select>
      </div>

      {/* Assign dropdown */}
      <div className="flex items-center gap-2">
        <span className="text-slate-400 text-xs">Assigned:</span>
        <AssignPicker
          value={ticket.assigned_admin_id || null}
          admins={admins}
          disabled={saving === 'assign'}
          onChange={(id) => doAssign(id)}
        />
      </div>

      {/* Due date — themed calendar popover (see DueDatePicker.jsx) */}
      <div className="flex items-center gap-2" data-testid="ticket-due-date-row">
        <span className="text-slate-400 text-xs flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" /> Due date:
        </span>
        <DueDatePicker
          value={ticket.due_date || null}
          disabled={saving === 'due'}
          testId="ticket-due-date-picker"
          onChange={(iso) => doDueDate(iso)}
        />
      </div>

      {/* Linked order */}
      <div className="flex items-center gap-2">
        <span className="text-slate-400 text-xs">Linked order:</span>
        {ticket.linked_order_name ? (
          <>
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> {ticket.linked_order_name}
            </span>
            <button
              onClick={() => doLink(null)}
              disabled={saving === 'link'}
              className="text-xs text-red-400 hover:text-red-300 underline"
              data-testid="ticket-unlink-order-btn"
            >Unlink</button>
          </>
        ) : (
          <button
            onClick={() => setOrderPickerOpen(v => !v)}
            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
            data-testid="ticket-link-order-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" /> {orderPickerOpen ? 'Cancel' : 'Link order'}
          </button>
        )}
      </div>

      {orderPickerOpen && (
        <div className="w-full mt-1 p-3 bg-[#0f1611] border border-emerald-900/30 rounded-lg">
          <input
            type="text"
            placeholder="Search orders…"
            value={orderSearch}
            onChange={e => setOrderSearch(e.target.value)}
            className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-2 py-1 text-xs text-white mb-2 focus:outline-none focus:border-emerald-500"
            data-testid="ticket-order-search"
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filteredOrders.length === 0 && <p className="text-xs text-slate-500 italic">No orders for this company.</p>}
            {filteredOrders.map(o => (
              <button
                key={o.id}
                onClick={() => doLink(o.id)}
                disabled={saving === 'link'}
                className="w-full text-left px-2 py-1.5 text-xs text-slate-300 hover:bg-emerald-500/10 rounded"
                data-testid={`ticket-order-option-${o.id}`}
              >
                {o.product_name} {o.period && <span className="text-slate-500">· {o.period}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blocked by — optional pointer to another order the team is waiting on.
          Set manually by admins; adds a system entry to the activity feed. */}
      <div className="w-full flex items-center gap-2" data-testid="ticket-blocked-by-row">
        <span className="text-slate-400 text-xs flex items-center gap-1">
          <AlertOctagon className="w-3.5 h-3.5" /> Blocked by:
        </span>
        {ticket.blocked_by_order_name ? (
          <>
            {onNavigateToOrder ? (
              <button
                type="button"
                onClick={() => onNavigateToOrder(ticket.blocked_by_order_id)}
                className="text-xs text-red-300 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 hover:border-red-400 hover:underline transition-colors"
                title="Open blocking order"
                data-testid="ticket-blocked-by-open"
              >
                <Package className="w-3.5 h-3.5" /> {ticket.blocked_by_order_name}
              </button>
            ) : (
              <span className="text-xs text-red-300 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30">
                <Package className="w-3.5 h-3.5" /> {ticket.blocked_by_order_name}
              </span>
            )}
            <button
              onClick={() => doBlockedBy(null)}
              disabled={saving === 'blocked'}
              className="text-xs text-slate-400 hover:text-white underline"
              data-testid="ticket-clear-blocker-btn"
            >Clear</button>
          </>
        ) : (
          <button
            onClick={() => setBlockerPickerOpen(v => !v)}
            className="text-xs text-slate-400 hover:text-emerald-300 flex items-center gap-1"
            data-testid="ticket-set-blocker-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" /> {blockerPickerOpen ? 'Cancel' : 'Set blocker'}
          </button>
        )}
      </div>

      {blockerPickerOpen && !ticket.blocked_by_order_name && (
        <div className="w-full mt-1 p-3 bg-[#170f0f] border border-red-500/30 rounded-lg" data-testid="ticket-blocker-picker">
          <p className="text-[11px] text-slate-400 mb-2">
            Mark this thread as waiting on another order. A system note will be added to the timeline.
          </p>
          <input
            type="text"
            placeholder="Search orders in this company…"
            value={blockerSearch}
            onChange={e => setBlockerSearch(e.target.value)}
            className="w-full bg-[#0a0f0d] border border-red-500/30 rounded-md px-2 py-1 text-xs text-white mb-2 focus:outline-none focus:border-red-400"
            data-testid="ticket-blocker-search"
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filteredBlockerOrders.length === 0 && (
              <p className="text-xs text-slate-500 italic">
                {ticket.order_id ? 'No other orders available for this company.' : 'No orders for this company.'}
              </p>
            )}
            {filteredBlockerOrders.map(o => (
              <button
                key={o.id}
                onClick={() => doBlockedBy(o.id)}
                disabled={saving === 'blocked'}
                className="w-full text-left px-2 py-1.5 text-xs text-slate-300 hover:bg-red-500/10 rounded flex items-center gap-2"
                data-testid={`ticket-blocker-option-${o.id}`}
              >
                <Package className="w-3.5 h-3.5 text-slate-500" />
                <span className="flex-1">{o.product_name} {o.period && <span className="text-slate-500">· {o.period}</span>}</span>
                <span className="text-[10px] text-slate-500 uppercase">{o.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Send Reminder — email the client asking for their input */}
      <button
        type="button"
        onClick={doReminder}
        disabled={saving === 'reminder' || ticket.status === 'closed'}
        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-40"
        data-testid="ticket-send-reminder-btn"
        title="Email the client asking for their input on this ticket"
      >
        {saving === 'reminder' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
        Send Reminder
      </button>
    </div>
  );
};

export const TicketDetailView = ({ ticketId, isAdmin, onBack, onNavigateToCompany, onNavigateToOrder }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [companyOrders, setCompanyOrders] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  // Admin-only multi-file queue: [{ id, file, error?: string }]. Clients keep
  // the single-file `pendingFile` flow untouched (v1 scope).
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [attDownloading, setAttDownloading] = useState({});
  // Admin composer mode: 'public' (User & Team — client sees) or 'internal' (Team-only note).
  // Ignored for clients (they can only post public).
  const [composeMode, setComposeMode] = useState('public');
  const feedEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const t = await ticketsApi.get(ticketId);
      setTicket(t);
      if (isAdmin) {
        try {
          const [ad, ords] = await Promise.all([
            ticketsApi.listAdmins(),
            ticketsApi.listCompanyOrders(t.company_id),
          ]);
          setAdmins(ad);
          setCompanyOrders(ords);
        } catch { /* non-critical */ }
      }
    } catch (e) {
      toast.error(getApiError(e, 'Failed to load ticket'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticketId]);

  useEffect(() => {
    if (feedEndRef.current) feedEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [ticket?.activity?.length]);

  const handleReply = async () => {
    const text = replyText.trim();
    const hasBatch = isAdmin && pendingFiles.length > 0;
    if (!text && !pendingFile && !hasBatch) return;
    // Only admins can post internal notes; clients are always public.
    const visibility = isAdmin && composeMode === 'internal' ? 'internal' : 'public';
    setSending(true);
    try {
      let updated;
      if (hasBatch) {
        // ── Admin queue path: multi-file batch upload → ONE comment + N attachments.
        const withErrors = pendingFiles.filter(x => x.error);
        if (withErrors.length > 0) {
          toast.error('Some queued files have errors — remove them before sending');
          setSending(false);
          return;
        }
        const fd = new FormData();
        for (const p of pendingFiles) fd.append('files', p.file, p.file.name);
        if (text) fd.append('text', text);
        fd.append('visibility', visibility);
        try {
          const res = await api.post(`/tickets/${ticketId}/attach-batch`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          updated = res.data;
        } catch (err) {
          // Per-file error map from server → surface it on the offending queue rows.
          const detail = err?.response?.data?.detail;
          if (detail && typeof detail === 'object' && detail.file_errors) {
            const errMap = detail.file_errors;
            setPendingFiles(prev => prev.map((x, idx) => {
              const key = `${idx}:${x.file.name}`;
              return errMap[key] ? { ...x, error: errMap[key] } : x;
            }));
            toast.error('Server rejected one or more files — see the queue for details');
          } else {
            toast.error(getApiError(err, 'Failed to send attachments'));
          }
          setSending(false);
          return;
        }
      } else if (pendingFile) {
        // Legacy single-file path (still used by the client composer).
        const fd = new FormData();
        fd.append('file', pendingFile);
        if (text) fd.append('text', text);
        fd.append('visibility', visibility);
        const res = await api.post(`/tickets/${ticketId}/attach`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        updated = res.data;
      } else {
        updated = await ticketsApi.reply(ticketId, text, visibility);
      }
      setTicket(prev => ({ ...(prev || {}), ...updated }));
      setReplyText('');
      setPendingFile(null);
      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      toast.error(getApiError(e, 'Failed to post comment'));
    } finally {
      setSending(false);
    }
  };

  // ── Admin queue actions (paste-to-attach, drag-drop, multi-select) ──
  const enqueueFiles = (fileList) => {
    if (!isAdmin) return; // safeguard: client keeps single-file flow
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setPendingFiles(prev => {
      const combined = [...prev];
      let dropped = 0;
      for (const raw of arr) {
        if (combined.length >= MAX_BATCH_FILES) { dropped++; continue; }
        // Rename pasted screenshots that came in as image.png → paste-<ts>.png
        let file = raw;
        if (raw.type && raw.type.startsWith('image/') && (raw.name === 'image.png' || !raw.name)) {
          try {
            file = new File([raw], _pasteFilename(), { type: raw.type });
          } catch { /* File ctor unsupported — keep original */ }
        }
        combined.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          error: _validateBatchFile(file),
        });
      }
      if (dropped > 0) toast.error(`Max ${MAX_BATCH_FILES} files per comment — ${dropped} dropped`);
      return combined;
    });
  };
  const removeFromQueue = (id) => setPendingFiles(prev => prev.filter(x => x.id !== id));

  const handleComposerPaste = (e) => {
    if (!isAdmin) return;
    const dt = e.clipboardData;
    if (!dt) return;
    let files = Array.from(dt.files || []);
    if (files.length === 0) {
      // Fallback for browsers that put clipboard files under `items` (Chrome).
      const items = Array.from(dt.items || []);
      files = items.filter(it => it.kind === 'file').map(it => it.getAsFile()).filter(Boolean);
    }
    if (files.length > 0) {
      e.preventDefault(); // don't paste an "image path" text alongside
      enqueueFiles(files);
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} attached from clipboard`);
    }
  };
  const handleComposerDrop = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) enqueueFiles(files);
  };
  const handleComposerDragOver = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  };
  const handleComposerDragLeave = (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    // relatedTarget null = drag left the window
    if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
  };

  const handleDeleteMessage = async (index) => {
    if (!window.confirm('Delete this message? This action is logged and cannot be undone. The other participant will see a "Message deleted" placeholder in its place.')) return;
    try {
      const updated = await ticketsApi.deleteMessage(ticketId, index);
      setTicket(prev => ({ ...(prev || {}), ...updated }));
      toast.success('Message deleted');
    } catch (e) {
      toast.error(getApiError(e, 'Failed to delete message'));
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    setAttDownloading(prev => ({ ...prev, [attachmentId]: true }));
    try {
      const res = await api.get(`/tickets/${ticketId}/attachments/${attachmentId}/download-url`);
      const url = res.data?.url;
      if (!url) throw new Error('No download URL');
      // The presigned URL is generated server-side with ResponseContentDisposition=attachment,
      // so simply navigating the browser to it triggers a download with the correct filename.
      // We avoid `fetch()` here — S3 has no CORS config, so reading the blob would fail
      // even though the file itself is accessible.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener noreferrer';
      // Note: the `download` attribute is ignored by browsers for cross-origin URLs,
      // but S3's Content-Disposition header already forces the right filename.
      a.download = filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to download attachment'));
    } finally {
      setAttDownloading(prev => ({ ...prev, [attachmentId]: false }));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }
  if (!ticket) return <p className="text-slate-400">Ticket not found.</p>;

  const activity = ticket.activity || [];

  return (
    <div className="max-w-4xl mx-auto" data-testid="ticket-detail-view">
      {onBack && (
        <button onClick={onBack} className="text-xs text-slate-400 hover:text-white mb-4" data-testid="ticket-detail-back">
          ← Back to tickets
        </button>
      )}

      {/* Title */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight" data-testid="ticket-subject">
          {ticket.subject}
        </h1>
        <StatusBadge status={ticket.status} />
      </div>

      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 mb-2" data-testid="ticket-metadata">
        {ticket.company_name ? (
          onNavigateToCompany ? (
            <button
              onClick={() => onNavigateToCompany({ id: ticket.company_id, company_name: ticket.company_name })}
              className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 hover:underline"
              data-testid="ticket-meta-company"
            >
              <Building2 className="w-3.5 h-3.5" /> {ticket.company_name}
            </button>
          ) : (
            <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {ticket.company_name}</span>
          )
        ) : (
          <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> —</span>
        )}
        <span data-testid="ticket-conversation-id" className="font-mono text-emerald-400">{ticket.conversation_id || `#${ticket.ticket_number}`}</span>
        <span className="flex items-center gap-1" data-testid="ticket-meta-creator">
          <UserIcon className="w-3.5 h-3.5" /> {ticket.created_by?.name} · {ticket.created_by?.user_type === 'admin' ? 'Admin' : 'Client'}
        </span>
        {ticket.linked_order_name && ticket.order_id && (
          onNavigateToOrder ? (
            <button
              onClick={() => onNavigateToOrder(ticket.order_id)}
              className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 hover:underline"
              data-testid="ticket-meta-order"
            >
              <Package className="w-3.5 h-3.5" /> {ticket.linked_order_name}
            </button>
          ) : (
            <span className="flex items-center gap-1 text-emerald-400" data-testid="ticket-meta-order">
              <Package className="w-3.5 h-3.5" /> {ticket.linked_order_name}
            </span>
          )
        )}
        {ticket.assigned_admin_name && !isAdmin && (
          <span className="flex items-center gap-1">Assigned to {ticket.assigned_admin_name}</span>
        )}
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Created {formatTime(ticket.created_at)}</span>
      </div>

      {/* Admin-only controls */}
      {isAdmin && <AdminControls ticket={ticket} onRefresh={load} admins={admins} orders={companyOrders} onNavigateToOrder={onNavigateToOrder} />}

      {/* Blocked-by callout — read-only for clients so they know why this thread
          isn't moving forward. Admins see the same block PLUS the picker in AdminControls above. */}
      {ticket.blocked_by_order_name && !isAdmin && (
        <button
          type="button"
          onClick={() => onNavigateToOrder && onNavigateToOrder(ticket.blocked_by_order_id)}
          disabled={!onNavigateToOrder}
          className={`mt-4 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-left ${onNavigateToOrder ? 'hover:bg-red-500/15 hover:border-red-400 cursor-pointer' : 'cursor-default'}`}
          data-testid="ticket-blocked-by-callout"
          title={onNavigateToOrder ? 'Open the blocking order' : ''}
        >
          <AlertOctagon className="w-4 h-4 text-red-400 flex-shrink-0" />
          <div className="text-sm text-slate-200 leading-tight flex-1">
            <span className="text-red-300 font-semibold">Blocked by:</span>{' '}
            <span className={onNavigateToOrder ? 'underline' : ''}>{ticket.blocked_by_order_name}</span>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Our team is waiting on this order to complete before we can move this conversation forward.
            </div>
          </div>
        </button>
      )}

      {/* Description block */}
      <div className="mt-5 p-4 bg-[#0f1611] border border-emerald-900/30 rounded-lg" data-testid="ticket-description">
        <p className="text-[13px] text-slate-500 mb-2 font-medium uppercase tracking-wide">Description</p>
        <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
      </div>

      {/* Activity feed */}
      <div className="mt-6" data-testid="ticket-activity-feed">
        <p className="text-[13px] text-slate-500 mb-3 font-medium uppercase tracking-wide flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> Activity
        </p>
        <div className="space-y-3">
          {activity.map((entry, i) => {
            if (entry.type === 'system') {
              const fromOrder = entry.source === 'order';
              return (
                <div
                  key={i}
                  className={
                    fromOrder
                      ? 'text-xs pl-2 py-1 border-l-2 border-emerald-500/30 bg-emerald-500/[0.03] rounded-r'
                      : 'text-xs text-slate-500 italic pl-1 py-1'
                  }
                  data-testid={`ticket-activity-system-${i}`}
                >
                  {fromOrder && (
                    <span className="inline-block mr-1.5 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-400 border border-emerald-500/30 rounded uppercase tracking-wider">
                      Order
                    </span>
                  )}
                  <span className={fromOrder ? 'text-slate-300' : 'text-slate-600'}>{entry.text}</span>
                  {fromOrder && entry.actor_name && entry.actor_name !== 'Order' && (
                    <span className="text-emerald-500/70 ml-1">· {entry.actor_name}</span>
                  )}
                  <span className={fromOrder ? 'text-slate-500 ml-2' : 'text-slate-700 ml-2'}>· {formatTime(entry.timestamp)}</span>
                </div>
              );
            }
            // Deleted comment placeholder — same slot in the timeline so context isn't lost.
            if (entry.deleted) {
              return (
                <div
                  key={i}
                  className={`flex ${entry.actor_type === 'admin' ? 'justify-start' : 'justify-end'}`}
                  data-testid={`ticket-activity-comment-deleted-${i}`}
                >
                  <div className="max-w-[80%] rounded-lg px-3 py-2 bg-slate-800/30 border border-dashed border-slate-700/50 text-slate-500 italic text-xs flex items-center gap-2">
                    <Trash2 className="w-3 h-3" />
                    <span>Message deleted{entry.deleted_by ? ` by ${entry.deleted_by}` : ''}{entry.deleted_at ? ` · ${formatTime(entry.deleted_at)}` : ''}</span>
                  </div>
                </div>
              );
            }
            const isInternal = (entry.visibility || 'public') === 'internal';
            // Internal notes render as a full-width amber sticky-note callout so admins can't
            // mistake them for a client-visible reply. Only admins should ever see these.
            if (isInternal) {
              return (
                <div key={i} className="flex justify-start group" data-testid={`ticket-activity-comment-${i}`}>
                  <div className="max-w-[85%] rounded-lg px-3.5 py-2.5 bg-amber-500/10 border-l-4 border-amber-500 border-y border-r border-amber-500/25 text-slate-100 relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-bold text-amber-300 tracking-wider uppercase">Internal Note · Team-only</span>
                      <span className="text-[10px] text-slate-500 ml-auto">{formatTime(entry.timestamp)}</span>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteMessage(i); }}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-500 hover:text-red-400 transition-opacity ml-1"
                          title="Delete this internal note (admin only, logged)"
                          data-testid={`ticket-delete-message-${i}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold text-amber-200">{entry.actor_name}</span>
                    </div>
                    {entry.text && <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-100">{entry.text}</p>}
                    {(entry.attachments || []).length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`ticket-attachments-${i}`}>
                        {entry.attachments.map(att => {
                          const isImg = (att.mime_type || '').startsWith('image/');
                          return (
                            <button
                              key={att.id}
                              onClick={() => handleDownloadAttachment(att.id, att.filename)}
                              disabled={attDownloading[att.id]}
                              className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-amber-950/40 border border-amber-500/30 hover:border-amber-400/60 w-full text-left"
                              data-testid={`ticket-attachment-${att.id}`}
                            >
                              {isImg ? <ImageIcon className="w-3.5 h-3.5 text-amber-400" /> : <FileText className="w-3.5 h-3.5 text-amber-400" />}
                              <span className="truncate flex-1 text-slate-100">{att.filename}</span>
                              <span className="text-[10px] text-slate-400">{Math.round((att.size || 0) / 1024)} KB</span>
                              {attDownloading[att.id]
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-300" />
                                : <Download className="w-3.5 h-3.5 text-slate-300" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div
                key={i}
                className={`flex group ${entry.actor_type === 'admin' ? 'justify-start' : 'justify-end'}`}
                data-testid={`ticket-activity-comment-${i}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-3.5 py-2.5 relative ${
                    entry.actor_type === 'admin'
                      ? 'bg-emerald-500/10 border border-emerald-500/25 text-slate-100'
                      : 'bg-slate-800/60 border border-slate-700/50 text-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-semibold text-emerald-400">
                      {entry.actor_name}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      · {entry.actor_type === 'admin' ? 'Support' : 'Client'}
                    </span>
                    <span className="text-[10px] text-slate-600 ml-auto">{formatTime(entry.timestamp)}</span>
                    {isAdmin && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMessage(i); }}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-500 hover:text-red-400 transition-opacity ml-1"
                        title={`Delete this ${entry.actor_type === 'admin' ? 'admin' : 'client'} message (admin only, logged)`}
                        data-testid={`ticket-delete-message-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {entry.text && <p className="text-sm whitespace-pre-wrap leading-relaxed">{entry.text}</p>}
                  {(entry.attachments || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`ticket-attachments-${i}`}>
                      {entry.attachments.map(att => {
                        const isImg = (att.mime_type || '').startsWith('image/');
                        return (
                          <button
                            key={att.id}
                            onClick={() => handleDownloadAttachment(att.id, att.filename)}
                            disabled={attDownloading[att.id]}
                            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-slate-900/50 border border-slate-700/50 hover:border-emerald-500/50 w-full text-left"
                            data-testid={`ticket-attachment-${att.id}`}
                          >
                            {isImg ? <ImageIcon className="w-3.5 h-3.5 text-emerald-400" /> : <FileText className="w-3.5 h-3.5 text-emerald-400" />}
                            <span className="truncate flex-1 text-slate-200">{att.filename}</span>
                            <span className="text-[10px] text-slate-500">{Math.round((att.size || 0) / 1024)} KB</span>
                            {attDownloading[att.id]
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                              : <Download className="w-3.5 h-3.5 text-slate-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={feedEndRef} />
        </div>
      </div>

      {/* Comment box */}
      {ticket.status !== 'closed' ? (
        <div className="mt-6 sticky bottom-0 bg-[#0a0f0d] pt-3" data-testid="ticket-reply-box">
          {/* Admin-only: toggle between client-visible reply and internal (Team-only) note */}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-2" data-testid="ticket-compose-mode-toggle">
              <button
                type="button"
                onClick={() => setComposeMode('public')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${composeMode === 'public' ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300' : 'bg-transparent border-slate-700 text-slate-400 hover:text-white'}`}
                data-testid="ticket-compose-mode-public"
              >
                <MessageCircle className="w-3 h-3" />
                Reply to client
              </button>
              <button
                type="button"
                onClick={() => setComposeMode('internal')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${composeMode === 'internal' ? 'bg-amber-500/15 border-amber-500/50 text-amber-300' : 'bg-transparent border-slate-700 text-slate-400 hover:text-white'}`}
                data-testid="ticket-compose-mode-internal"
              >
                <Lock className="w-3 h-3" />
                Internal note
              </button>
              <span className="text-[10px] text-slate-500 ml-1">
                {composeMode === 'internal' ? 'Only your team will see this — the client will NOT be notified.' : 'The client will see this reply and receive an email.'}
              </span>
            </div>
          )}
          {/* Legacy single-file pending chip — CLIENT-ONLY. Admin uses the queue below. */}
          {!isAdmin && pendingFile && (
            <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs" data-testid="ticket-pending-attachment">
              <Paperclip className="w-3.5 h-3.5 text-emerald-400" />
              <span className="flex-1 truncate text-slate-200">{pendingFile.name}</span>
              <span className="text-slate-500">{Math.round(pendingFile.size / 1024)} KB</span>
              <button onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-slate-400 hover:text-red-400" data-testid="ticket-remove-pending-attachment">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Admin-only: multi-file queue with per-card size/error/remove. */}
          {isAdmin && pendingFiles.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="ticket-attachment-queue">
              {pendingFiles.map(p => {
                const ico = _iconFor(p.file.name, p.file.type);
                const hasErr = !!p.error;
                return (
                  <div
                    key={p.id}
                    className={`relative flex items-center gap-2 px-2 py-1.5 pr-6 rounded-md border text-xs max-w-[240px] ${hasErr ? 'bg-red-500/10 border-red-500/40' : 'bg-[#0f1611] border-emerald-900/40'}`}
                    data-testid={`ticket-queue-item-${p.id}`}
                    title={hasErr ? p.error : p.file.name}
                  >
                    <span className={`inline-flex items-center justify-center w-6 h-7 rounded text-[9px] font-bold text-white flex-none ${ico.cls}`}>
                      {ico.label}
                    </span>
                    <span className="flex flex-col min-w-0">
                      <span className="truncate text-slate-200 max-w-[140px]">{p.file.name}</span>
                      <span className={`text-[10px] ${hasErr ? 'text-red-300' : 'text-slate-500'}`}>
                        {hasErr ? p.error : _fmtSize(p.file.size)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFromQueue(p.id)}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 text-white text-[10px] flex items-center justify-center hover:bg-red-500"
                      data-testid={`ticket-queue-remove-${p.id}`}
                      title="Remove from queue"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <span className="self-center text-[10px] text-slate-500 ml-1">
                {pendingFiles.length}/{MAX_BATCH_FILES} · max {MAX_BATCH_MB} MB each
              </span>
            </div>
          )}

          {/* Drop-zone wrapper (admin gets drag-drop; harmless no-op for clients). */}
          <div
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
            onDrop={handleComposerDrop}
            className={`flex gap-2 rounded-lg transition-colors ${dragActive ? 'ring-2 ring-emerald-500/60 bg-emerald-500/5' : ''}`}
            data-testid="ticket-composer-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple={isAdmin}
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length === 0) return;
                if (isAdmin) {
                  enqueueFiles(files);
                } else {
                  // Client path: single-file only (unchanged v1 behaviour)
                  const f = files[0];
                  if (f.size > 20 * 1024 * 1024) { toast.error('File exceeds 20 MB limit'); e.target.value = ''; return; }
                  setPendingFile(f);
                }
                // Clear the picker so re-selecting the same file fires onChange again
                e.target.value = '';
              }}
              data-testid="ticket-attach-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title={isAdmin ? `Attach files (max ${MAX_BATCH_FILES}, ${MAX_BATCH_MB} MB each)` : 'Attach a file (max 20 MB)'}
              className="self-end p-2 rounded-md border border-emerald-900/40 bg-[#0f1611] text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40 disabled:opacity-50"
              data-testid="ticket-attach-btn"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onPaste={isAdmin ? handleComposerPaste : undefined}
              placeholder={
                isAdmin && composeMode === 'internal'
                  ? 'Note for the team — hidden from the client…'
                  : isAdmin
                    ? (pendingFiles.length > 0 ? 'Optional message with your attachments…' : 'Add a comment — paste screenshots or drop files here')
                    : (pendingFile ? 'Optional message with your attachment…' : 'Add a comment…')
              }
              rows={2}
              className={`flex-1 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none resize-none ${
                isAdmin && composeMode === 'internal'
                  ? 'bg-amber-500/5 border border-amber-500/50 focus:border-amber-400'
                  : 'bg-[#0f1611] border border-emerald-900/30 focus:border-emerald-500'
              }`}
              data-testid="ticket-reply-input"
            />
            <Button
              onClick={handleReply}
              disabled={sending || (!replyText.trim() && !pendingFile && !(isAdmin && pendingFiles.length > 0))}
              className={`self-end ${isAdmin && composeMode === 'internal' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              data-testid="ticket-reply-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-6 p-3 bg-slate-800/40 border border-slate-700/40 rounded-lg text-center text-xs text-slate-500" data-testid="ticket-closed-notice">
          <X className="w-3.5 h-3.5 inline mr-1" /> This ticket is closed. An admin must re-open it before further replies.
        </div>
      )}
    </div>
  );
};

export default TicketDetailView;
